import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiUploadCloud } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { CATEGORY_GET, CATEGORY_UPDATE } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import MultiSelect from '../MultiSelect';
import './Page.css';
import './CreateCategory.css';

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const AGE_GROUP_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

function EditCategory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('Active');
  const [age_groups, setAge_groups] = useState([]);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('Invalid category.');
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(CATEGORY_GET.pathFor(id)), { method: CATEGORY_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Parent category not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setCategory(data);
        setName(data.name ?? '');
        setStatus(data.status === 'Inactive' ? 'Inactive' : 'Active');
        setAge_groups(Array.isArray(data.age_groups) ? data.age_groups : []);
        if (data.thumbnailUrl) setThumbnailPreview(data.thumbnailUrl);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load category.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    return () => {
      if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
    };
  }, [thumbnailPreview]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError('');
    if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) {
      URL.revokeObjectURL(thumbnailPreview);
    }
    if (!file) {
      setThumbnailFile(null);
      setThumbnailPreview(category?.thumbnailUrl ?? null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (e.g. JPG, PNG).');
      setThumbnailFile(null);
      setThumbnailPreview(category?.thumbnailUrl ?? null);
      return;
    }
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Parent category name is required.');
      return;
    }
    if (!id) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      formData.append('name', trimmedName);
      formData.append('status', status);
      age_groups.forEach((v) => formData.append('age_groups', v));
      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

      const res = await fetch(apiUrl(CATEGORY_UPDATE.pathFor(id)), {
        method: CATEGORY_UPDATE.method,
        headers,
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }

      navigate('/dashboard/cms/category');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page create-category-page">
        <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/category')}>
          <FiArrowLeft className="back-icon" aria-hidden />
          Back to Parent categories
        </button>
        <p className="create-category-loading">Loading category…</p>
      </div>
    );
  }

  if (error && !category) {
    return (
      <div className="page create-category-page">
        <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/category')}>
          <FiArrowLeft className="back-icon" aria-hidden />
          Back to Parent categories
        </button>
        <div className="create-category-error" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/category')}>
        <FiArrowLeft className="back-icon" aria-hidden />
        Back to Parent categories
      </button>

      <div className="create-category-header">
        <h3>Edit parent category</h3>
        <p className="create-category-subtitle">
          Update name, thumbnail or status. Leave thumbnail unchanged unless you choose a new image.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="create-category-form">
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="edit-category-name">Parent category name <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="edit-category-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Nursery Rhymes"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-category-status">Status</label>
                <select
                  id="edit-category-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={isSubmitting}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single form-row-full">
                <label>Age groups</label>
                <MultiSelect
                  options={AGE_GROUP_OPTIONS}
                  value={age_groups}
                  onChange={setAge_groups}
                  placeholder="Select age groups"
                  disabled={isSubmitting}
                  searchable={false}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Thumbnail</h4>
            <div className="form-thumbnail-with-preview">
              <div className="thumbnail-upload-area">
                <input
                  type="file"
                  id="edit-thumbnail-upload"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  className="thumbnail-input"
                />
                <label htmlFor="edit-thumbnail-upload" className="thumbnail-upload-label">
                  <FiUploadCloud className="upload-icon" aria-hidden />
                  <span>{thumbnailFile ? thumbnailFile.name : 'Choose new image (optional)'}</span>
                </label>
              </div>
              {thumbnailPreview && (
                <div className="thumbnail-preview-wrap">
                  <img src={thumbnailPreview} alt="Thumbnail preview" className="thumbnail-preview-img" />
                </div>
              )}
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/cms/category')} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditCategory;
